import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The missing and uploaded paths of a whole preview shell, for one panel. A
 * context, not an event subscription: each consumer knows its address is
 * needed and reports it through `useMissingReport`. With no provider the
 * actions do nothing and the sets are empty, so a hook calls `report` without
 * a null check.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { resourceFilePath } from '../../resources/subResourcePath.js';
import { useCountedReports } from '../hooks/useCountedReports.js';
import { withKey, withoutKey } from '../../utils/keySet.js';
const NOOP = {
    missingPaths: new Set(),
    uploadedPaths: new Set(),
    report: () => { },
    clear: () => { },
    markUploaded: () => { },
    removeUploaded: () => { },
};
const MissingResourcesContext = createContext(NOOP);
MissingResourcesContext.displayName = 'MissingResourcesContext';
export function MissingResourcesProvider({ children, onMissingPathsChange }) {
    // Several slots can consume one path, so it stays missing while any of them reports it. An effect
    // on the whole context object loops, so a consumer takes the actions by name.
    const { reported: missingPaths, report, clear, clearAll } = useCountedReports();
    const [uploadedPaths, setUploadedPaths] = useState(() => new Set());
    const markUploaded = useCallback((path) => {
        if (!path)
            return;
        // Only this place knows the two keys. `missingPaths` holds resource identities,
        // a **Sub-resource path** included. `uploadedPaths` holds files, what the user
        // supplied. One `.tres` behind three materials clears three missing rows and
        // shows one uploaded row.
        setUploadedPaths((prev) => withKey(prev, resourceFilePath(path)));
        // An upload heals every consumer of the path at once, so all its reports go.
        clearAll(path);
    }, [clearAll]);
    /** Takes a file, the key `markUploaded` stored. */
    const removeUploaded = useCallback((path) => {
        if (!path)
            return;
        setUploadedPaths((prev) => withoutKey(prev, path));
    }, []);
    // A ref, so notification keys only on the set's identity. A host callback in
    // the deps re-fires each render, and a host storing the set in state loops.
    const onMissingPathsChangeRef = useRef(onMissingPathsChange);
    useEffect(() => {
        onMissingPathsChangeRef.current = onMissingPathsChange;
    });
    useEffect(() => {
        onMissingPathsChangeRef.current?.(missingPaths);
    }, [missingPaths]);
    const value = useMemo(() => ({
        missingPaths,
        uploadedPaths,
        report,
        clear,
        markUploaded,
        removeUploaded,
    }), [missingPaths, uploadedPaths, report, clear, markUploaded, removeUploaded]);
    return _jsx(MissingResourcesContext.Provider, { value: value, children: children });
}
export function useMissingResources() {
    return useContext(MissingResourcesContext);
}
/**
 * Keeps `address` in `missingPaths` while `status` is `unavailable`. An address that loads after
 * this hook reported it was uploaded, so its row stays, marked uploaded, and keeps its Remove
 * control. An address that loads on first request never shows a row.
 */
export function useMissingReport(address, status) {
    const { report, clear, markUploaded } = useMissingResources();
    const reportedAddressRef = useRef(null);
    useEffect(() => {
        if (status === 'unavailable') {
            report(address);
            reportedAddressRef.current = address;
            return () => clear(address);
        }
        // The cleanup above already withdrew this hook's report, so a load clears nothing here.
        if (status === 'loaded' && reportedAddressRef.current === address) {
            markUploaded(address);
            reportedAddressRef.current = null;
        }
        return undefined;
    }, [address, status, report, clear, markUploaded]);
}
//# sourceMappingURL=MissingResourcesContext.js.map